import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthService, VehicleRepairService, VehicleService } from "../../services";
import { Upload } from "react-bootstrap-icons";
import { Breadcrumb } from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment";
import {
  QUANTITY_OPTIONS,
  truncateReceiptFileLabel,
} from "../../shared";

const EditRepairRecord = () => {
  const navigate = useNavigate();
  const params = useParams();

  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [partName, setPartName] = useState('');
  const [replacementDate, setReplacementDate] = useState(null);
  const [mileage, setMileage] = useState('');
  const [quantity, setQuantity] = useState('');
  const [cost, setCost] = useState('');
  const [partsCharges, setPartsCharges] = useState('');
  const [laborCharges, setLaborCharges] = useState('');
  const [tax, setTax] = useState('');
  const [location, setLocation] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [existingReceiptFiles, setExistingReceiptFiles] = useState([]);
  const [nextReminder, setNextReminder] = useState('');
  const [deleting, setDeleting] = useState(false);

  const normalizeCurrencyInput = (value = '') =>
    String(value).replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

  const formatCurrencyDisplay = (value = '') => {
    const normalized = normalizeCurrencyInput(value);
    if (!normalized) return '';
    const amount = Number(normalized);
    if (!Number.isFinite(amount)) return '';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const normalizeMileageInput = (value = '') => String(value).replace(/[^0-9]/g, '');

  const formatMileageDisplay = (value = '') => {
    const normalized = normalizeMileageInput(value);
    if (!normalized) return '';
    return Number(normalized).toLocaleString();
  };

  const loadReceiptFiles = (vehicleId, repairId) => {
    VehicleService.getAllVechileFiles(vehicleId, 'repair', 'repair')
      .then((res) => {
        const list = res?.data?.data?.files || [];
        const byId = VehicleService.indexRepairReceiptFiles(list);
        setExistingReceiptFiles(byId[String(repairId)] || []);
      })
      .catch(() => setExistingReceiptFiles([]));
  };

  useEffect(() => {
    if (!AuthService.canEditVehicleRepairs()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page.');
      AuthService.logout();
      navigate('/login');
    }
    VehicleService.getVehicle(params.id).then((res) => {
      const v = res.data;
      setCurrentVehicle(v);
      const vehicleId = v?.id;
      if (!vehicleId) return;
      VehicleRepairService.getAll(params.id).then((r2) => {
        const repair = (r2.data || []).find(
          (r) => String(r.id) === String(params.repairId) || String(r._id) === String(params.repairId)
        );
        if (repair) {
          setPartName(repair.part_name || '');
          if (repair.repair_date) {
            const parsed = moment(repair.repair_date, 'MM/DD/YYYY');
            if (parsed.isValid()) setReplacementDate(parsed.toDate());
          }
          setMileage(repair.mileage_at_replacement || '');
          setQuantity(repair.quantity || '');
          setCost(repair.repair_price || '');
          setPartsCharges(repair.parts_charges || '');
          setLaborCharges(repair.labor_charges || '');
          setTax(repair.tax || '');
          setLocation(repair.repair_location || '');
          setNextReminder(formatMileageDisplay(repair.next_replacement_reminder || ''));
          loadReceiptFiles(vehicleId, params.repairId);
        } else {
          setExistingReceiptFiles([]);
        }
      });
    });
  }, []);

  const formatDateForBackend = (date) => {
    if (!date) return '';
    return moment(date).format('MM/DD/YYYY');
  };

  const handleSave = () => {
    if (!partName || !partName.trim()) {
      window.alert('Please enter a part name.');
      return;
    }
    if (!replacementDate) {
      window.alert('Please select a replacement date.');
      return;
    }
    if (!cost || !normalizeCurrencyInput(cost)) {
      window.alert('Please enter total cost.');
      return;
    }
    if (!location || !location.trim()) {
      window.alert('Please enter a location.');
      return;
    }
    const data = {
      part_name: partName.trim(),
      part_name_other: '',
      repair_date: formatDateForBackend(replacementDate),
      mileage_at_replacement: formatMileageDisplay(mileage),
      quantity,
      repair_price: formatCurrencyDisplay(cost),
      parts_charges: formatCurrencyDisplay(partsCharges),
      labor_charges: formatCurrencyDisplay(laborCharges),
      tax: formatCurrencyDisplay(tax),
      repair_location: location.trim(),
      next_replacement_reminder: formatMileageDisplay(nextReminder)
    };
    VehicleRepairService.updateVehicleRepair(params.repairId, data).then(() => {
      if (receiptFile) {
        const fd = new FormData();
        fd.append('file', receiptFile);
        VehicleService.uploadVechileFile(
          fd,
          params.id,
          params.repairId,
          'repair',
          replacementDate || new Date()
        )
          .then(() => {
            goBack();
          })
          .catch((uploadErr) => {
            console.error('Receipt upload failed:', uploadErr);
            const msg =
              uploadErr?.response?.data?.message || uploadErr?.message || 'Upload failed';
            window.alert('Repairs were updated, but the receipt could not be uploaded: ' + msg);
            goBack();
          });
      } else {
        goBack();
      }
    });
  };

  const goBack = () => {
    navigate(`/vehicles/${params.id}?tab=documents`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this repair record? This cannot be undone.')) {
      return;
    }
    setDeleting(true);
    try {
      await VehicleRepairService.deleteVehicleRepair(params.repairId);
      goBack();
    } catch (error) {
      console.error('Delete repair record failed:', error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete repair record.';
      window.alert(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/trans-routes/dashboard">Transportation</Breadcrumb.Item>
          <Breadcrumb.Item href="/vehicles/list">Vehicles Information</Breadcrumb.Item>
          <Breadcrumb.Item href={`/vehicles/${params.id}?tab=documents`}>View Vehicle Information</Breadcrumb.Item>
          <Breadcrumb.Item active>Edit Repair Record</Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4>Edit Repair Record <button className="btn btn-link btn-sm" onClick={goBack}>Back</button></h4>
        </div>
      </div>
      <div className="app-main-content-list-container form-page">
        <div className="app-main-content-list-func-container">
          <h6 className="text-primary">Repair & Maintenance Record</h6>

          <div className="app-main-content-fields-section">
            <div className="me-4">
              <div className="field-label">Part Name<span className="required">*</span></div>
              <input
                type="text"
                placeholder="e.g., Brake pad"
                value={partName}
                onChange={e => setPartName(e.target.value)}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Replacement Date<span className="required">*</span></div>
              <DatePicker
                selected={replacementDate}
                onChange={(date) => setReplacementDate(date)}
                dateFormat="MM/dd/yyyy"
                placeholderText="e.g., 03/01/2024"
                className="form-control"
              />
            </div>
            <div className="me-4">
              <div className="field-label">Mileage at Replacement</div>
              <input
                type="text"
                placeholder="e.g., 48,000"
                value={mileage}
                onFocus={(e) => setMileage(normalizeMileageInput(e.target.value))}
                onChange={(e) => setMileage(normalizeMileageInput(e.target.value))}
                onBlur={(e) => setMileage(formatMileageDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Quantity</div>
              <select value={quantity} onChange={e => setQuantity(e.target.value)}>
                <option value="">Select...</option>
                {QUANTITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="app-main-content-fields-section">
            <div className="me-4">
              <div className="field-label">Total Cost<span className="required">*</span></div>
              <input
                type="text"
                placeholder="e.g., $250.00"
                value={cost}
                onFocus={(e) => setCost(normalizeCurrencyInput(e.target.value))}
                onChange={(e) => setCost(normalizeCurrencyInput(e.target.value))}
                onBlur={(e) => setCost(formatCurrencyDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Parts Charges</div>
              <input
                type="text"
                placeholder="e.g., $50.00"
                value={partsCharges}
                onFocus={(e) => setPartsCharges(normalizeCurrencyInput(e.target.value))}
                onChange={(e) => setPartsCharges(normalizeCurrencyInput(e.target.value))}
                onBlur={(e) => setPartsCharges(formatCurrencyDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Labor Charges</div>
              <input
                type="text"
                placeholder="e.g., $50.00"
                value={laborCharges}
                onFocus={(e) => setLaborCharges(normalizeCurrencyInput(e.target.value))}
                onChange={(e) => setLaborCharges(normalizeCurrencyInput(e.target.value))}
                onBlur={(e) => setLaborCharges(formatCurrencyDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Tax</div>
              <input
                type="text"
                placeholder="e.g., $50.00"
                value={tax}
                onFocus={(e) => setTax(normalizeCurrencyInput(e.target.value))}
                onChange={(e) => setTax(normalizeCurrencyInput(e.target.value))}
                onBlur={(e) => setTax(formatCurrencyDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Location<span className="required">*</span></div>
              <input type="text" placeholder="e.g., Rockville Auto Center" value={location} onChange={e => setLocation(e.target.value)} />
            </div>
            <div className="me-4">
              <div className="field-label">Next Replacement Reminder</div>
              <input
                type="text"
                placeholder="e.g., 78,000"
                value={nextReminder}
                onFocus={(e) => setNextReminder(normalizeMileageInput(e.target.value))}
                onChange={(e) => setNextReminder(normalizeMileageInput(e.target.value))}
                onBlur={(e) => setNextReminder(formatMileageDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Receipt Upload</div>
              {existingReceiptFiles.length > 0 && (
                <div className="mb-1 small text-muted">
                  Current:{' '}
                  {existingReceiptFiles.map((f, i) => (
                    <a
                      key={`${f.name}-${i}`}
                      href={VehicleService.getPhysicalFileDownloadUrl(f.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="me-2"
                      title={f.name}
                    >
                      {truncateReceiptFileLabel(f.name)}
                    </a>
                  ))}
                </div>
              )}
              <label className="custom-file-upload">
                <Upload width={20} color={"#fff"} className="me-2" /> Upload
                <input type="file" onChange={(e) => setReceiptFile(e.target.files[0])} />
              </label>
              <div className="file-name" title={receiptFile?.name}>
                {receiptFile?.name ? truncateReceiptFileLabel(receiptFile.name) : ""}
              </div>
            </div>
          </div>

          <div className="list row mb-5 mt-3">
            <div className="col-md-12 col-sm-12 col-xs-12 d-flex flex-wrap align-items-center justify-content-between gap-2">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                disabled={deleting}
                onClick={() => void handleDelete()}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <div>
                <button
                  type="button"
                  className="btn btn-default btn-sm me-2"
                  disabled={deleting}
                  onClick={goBack}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={deleting}
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditRepairRecord;
